import { useCallback, useMemo, useState } from "react";

import copyToClipboard from "copy-to-clipboard";
import { useHistory } from "react-router-dom";

import ROUTES from "manager/config/routes";
import { useGetApplicationAuditsCount } from "manager/hooks/api";
import { useGetApplicationSummary } from "manager/hooks/application-summary/useGetApplicationSummary";

import { NOTIFICATIONS } from "shared/config/constants";
import { useDeviceType } from "shared/hooks";
import useModal from "shared/hooks/useModal";
import { useVeroFlag } from "shared/utils/featureFlags";
import { transformRoute } from "shared/utils/routing";
import { openNotification } from "shared/utils/ui";
import { VeroFlag } from "shared/utils/veroFlags";
import { setPrefix } from "shared/utils/vrn";

import { DealInviteApplicantsModal } from "../Modal";
import { useOpenCreateDealAuditNoteModal } from "../SystemAuditsTable";

import { mapToHeaderDeal } from "./header";
import { isCanceledDeal, isDeclinedDeal } from "./header/utils";
import { getPrimaryApplicantDealFromRawApplications } from "./use-app-summary.utils";
import { mapDealToApplicantsSummary, mapDealToProps } from "./utils";

export const useAppSummary = (dealId: string, company?: any) => {
  const {
    deal,
    rawFullApplications,
    isLoading: loading,
    dealNotFound,
    dealFetchFailed,
    dealRefetch,
  } = useGetApplicationSummary({ dealId, companySlug: company?.slug });

  const { lumenResult, lumenRevision, applicantsSummaryShort } = deal ?? {};
  const treatCondApprovedAsSubmitted = useVeroFlag(
    VeroFlag.allowMoreActionOptions
  );
  const pmsApprovedNoVeroEnabled = useVeroFlag(
    VeroFlag.useYardiApprovedNoVeroStatus
  );

  const { applicationAuditsCount } = useGetApplicationAuditsCount(dealId, {
    enabled: Boolean(deal?.id) && !dealNotFound,
  });
  const refetchDeal = () => {
    dealRefetch();
  };

  const primaryApplicantDeal = useMemo(
    () => getPrimaryApplicantDealFromRawApplications(rawFullApplications ?? []),
    [rawFullApplications]
  );

  const preferredLeaseDurationMonths =
    primaryApplicantDeal?.preferredLeaseDurationMonths;
  const rent = deal?.fees?.rent;
  const unitListingDeal = deal?.unitListing;

  const { isMobile } = useDeviceType();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const history = useHistory();
  const { openModalDialog } = useModal();
  const { openCreateAuditNoteModal } = useOpenCreateDealAuditNoteModal({
    dealId,
  });

  const contactAll = useCallback(() => {
    if (!deal) return;
    copyToClipboard(deal.applications.map((e) => e.applicant.email).join(", "));
    openNotification(
      "Applicants' emails copied to clipboard. Open your email client and paste values to contact applicants.",
      NOTIFICATIONS.info
    );
  }, [deal]);
  const addNotes = () => openCreateAuditNoteModal();
  const invite = () =>
    openModalDialog(DealInviteApplicantsModal, {
      dealId,
      refetchDeal,
      applicantsSummary: mapDealToApplicantsSummary(deal),
    });

  const isInactiveDealValue =
    deal?.status &&
    (isCanceledDeal(deal.status) || isDeclinedDeal(deal.status));

  const goBack = () => {
    history.push(transformRoute(ROUTES.deals, { id: dealId }));
  };

  const headerProps = useMemo(
    () => mapToHeaderDeal(deal, dealId, unitListingDeal, primaryApplicantDeal),
    [deal, dealId, unitListingDeal, primaryApplicantDeal]
  );

  const {
    dealModalProps,
    title,
    appWellProps,
    apps,
    actionBarProps,
    deps,
    countyRecordsProps,
  } = useMemo(
    () =>
      mapDealToProps(
        dealId,
        deal,
        preferredLeaseDurationMonths,
        rent,
        lumenRevision,
        lumenResult,
        applicantsSummaryShort,
        treatCondApprovedAsSubmitted,
        pmsApprovedNoVeroEnabled
      ),
    [
      deal,
      dealId,
      preferredLeaseDurationMonths,
      rent,
      lumenRevision,
      lumenResult,
      applicantsSummaryShort,
      treatCondApprovedAsSubmitted,
      pmsApprovedNoVeroEnabled,
    ]
  );

  const applicationVrn =
    deal?.companySlug && deal?.id
      ? setPrefix(`${deal.companySlug}/${deal.id}`, "application")
      : undefined;

  return {
    loading,
    dealNotFound,
    dealFetchFailed,
    apps,
    deps,
    appWellProps,
    dealModalProps,
    headerProps,
    actionBarProps,
    title,
    isMobile,
    isExpanded,
    contactAll,
    addNotes,
    invite: !isInactiveDealValue && invite,
    goBack,
    toggleExpanded,
    refetchDeal,
    countyRecordsProps,
    applicationAuditsCount,
    conditionalProceed: deal?.conditionalProceed,
    dealStatus: deal?.status,
    fraudIndicators: deal?.fraudIndicators,
    applicationVrn,
    applications: deal?.applications,
    rawFullApplications,
    lumenRevision,
  };
};
